import { useState } from 'react';
import { Church } from '../../../mocks/churches';
import ChurchModal from './ChurchModal';

interface ChurchCardProps {
  church: Church;
}

export default function ChurchCard({ church }: ChurchCardProps) {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleShare = (platform: string) => {
    const churchUrl = `${window.location.origin}/church/${church.id}`;
    const shareText = `Check out ${church.name} in ${church.location}`;

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(churchUrl)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(churchUrl)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + churchUrl)}`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(churchUrl)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(churchUrl).then(() => {
          setCopySuccess(true);
          setTimeout(() => {
            setCopySuccess(false);
            setShowShareMenu(false);
          }, 2000);
        });
        break;
    }

    if (platform !== 'copy') {
      setShowShareMenu(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 group">
        <div className="h-48 overflow-hidden">
          <img
            src={church.image}
            alt={church.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
          />
        </div>

        <div className="p-6">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-lg font-bold text-gray-900 flex-1 pr-2" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
              {church.name}
            </h3>
            {church.distance !== undefined && (
              <span className="px-2 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full whitespace-nowrap">
                {church.distance.toFixed(1)} km
              </span>
            )}
          </div>

          <div className="space-y-2 mb-4">
            <p className="text-sm text-gray-600 flex items-center">
              <i className="ri-map-pin-line text-emerald-600 mr-2"></i>
              {church.location}
            </p>
            <p className="text-sm text-gray-600 flex items-center">
              <i className="ri-building-line text-emerald-600 mr-2"></i>
              {church.conference}
            </p>
            <p className="text-sm text-gray-600 flex items-center">
              <i className="ri-organization-chart text-emerald-600 mr-2"></i>
              {church.union}
            </p>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500 flex items-center">
                <i className="ri-group-line mr-1"></i>
                {church.members}
              </span>
              {church.livestream && (
                <span className="text-xs text-red-600 flex items-center">
                  <i className="ri-live-line mr-1"></i>
                  Live
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowModal(true)}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                View Details
              </button>
              
              <div className="relative">
                <button
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-share-line"></i>
                </button>

                {showShareMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowShareMenu(false)}
                    ></div>
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                      <button
                        onClick={() => handleShare('facebook')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                      >
                        <i className="ri-facebook-fill text-blue-600"></i>
                        Facebook
                      </button>
                      <button
                        onClick={() => handleShare('twitter')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                      >
                        <i className="ri-twitter-x-fill text-gray-900"></i>
                        Twitter
                      </button>
                      <button
                        onClick={() => handleShare('whatsapp')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                      >
                        <i className="ri-whatsapp-fill text-green-600"></i>
                        WhatsApp
                      </button>
                      <button
                        onClick={() => handleShare('email')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                      >
                        <i className="ri-mail-line text-gray-600"></i>
                        Email
                      </button>
                      <div className="border-t border-gray-100 my-1"></div>
                      <button
                        onClick={() => handleShare('copy')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                      >
                        {copySuccess ? (
                          <>
                            <i className="ri-check-line text-green-600"></i>
                            <span className="text-green-600">Link Copied!</span>
                          </>
                        ) : (
                          <>
                            <i className="ri-link text-gray-600"></i>
                            Copy Link
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <ChurchModal church={church} onClose={() => setShowModal(false)} />
      )}
    </>
  );
}
